/**
 * ChainVault WP - Vault Market Page
 * Live cryptocurrency market data with ticker bar
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import './VaultMarketPage.css';

function AdSlot( { html } ) { if ( !html ) return null; return React.createElement( 'div', { className: 'cv-ad-slot', style: { margin: '16px 0', textAlign: 'center' } , dangerouslySetInnerHTML: { __html: html } } ); }
function getAdSlot2() { return ( window.chainvaultSettings && window.chainvaultSettings.adSlot2 ) || ''; }
const COINGECKO_URL = 'https://api.coingecko.com/api/v3';
const PROXY_URL = ( window.chainvaultSettings && window.chainvaultSettings.siteUrl ) ? window.chainvaultSettings.siteUrl.replace(/\/+$/, '') + '/market' : COINGECKO_URL;
const DOLLAR = String.fromCharCode(36);

function getSettings() { return window.chainvaultSettings || {}; }
function isTickerEnabled() { return window.chainvaultSettings && window.chainvaultSettings.tickerEnabled !== false; }
function getCoinGeckoHeaders() {
    const key = getSettings().coingeckoApiKey;
    return key ? { 'x-cg-demo-api-key': key } : {};
}

const CATEGORY_FILTERS = [
    { id: 'all',                        label: 'All'         },
    { id: 'decentralized-finance-defi', label: 'DeFi'        },
    { id: 'layer-1',                    label: 'Layer 1'     },
    { id: 'layer-2',                    label: 'Layer 2'     },
    { id: 'meme-token',                 label: 'Meme'        },
    { id: 'stablecoins',                label: 'Stablecoins' },
    { id: 'gaming',                     label: 'Gaming'      },
    { id: 'artificial-intelligence',    label: 'AI'          },
];

const TICKER_COINS = [
    { id: 'bitcoin',  symbol: 'BTC', color: '#F7931A' },
    { id: 'ethereum', symbol: 'ETH', color: '#627EEA' },
    { id: 'solana',   symbol: 'SOL', color: '#9945FF' },
    { id: 'ripple',   symbol: 'XRP', color: '#346AA9' },
];

/* ── Get affiliate buy link ── */
function getBuyLink() {
    const aff = window.chainvaultSettings?.affiliate;
    if ( !aff || aff.enabled !== '1' ) return null;
    const exchanges = aff.exchanges || [];
    const active = exchanges.find( e => e.active === '1' );
    return active?.link || exchanges[0]?.link || null;
}

function getBuyLabel() {
    return window.chainvaultSettings?.affiliate?.label || 'Buy Now';
}

function formatPrice( price ) {
    if ( !price && price !== 0 ) return '$0.00';
    if ( price >= 1000 ) return '$' + price.toLocaleString( 'en-US', { maximumFractionDigits: 2 } );
    if ( price >= 1    ) return '$' + price.toFixed( 2 );
    if ( price >= 0.01 ) return '$' + price.toFixed( 4 );
    return '$' + price.toFixed( 8 );
}

function formatLarge( num ) {
    if ( !num ) return '-';
    if ( num >= 1e12 ) return '$' + ( num / 1e12 ).toFixed( 2 ) + 'T';
    if ( num >= 1e9  ) return '$' + ( num / 1e9  ).toFixed( 2 ) + 'B';
    if ( num >= 1e6  ) return '$' + ( num / 1e6  ).toFixed( 2 ) + 'M';
    return '$' + num.toLocaleString();
}

function SparklineChart( { data, positive } ) {
    if ( !data || data.length < 2 ) return null;
    const min = Math.min( ...data );
    const max = Math.max( ...data );
    const range = max - min || 1;
    const w = 80; const h = 36;
    const points = data.map( ( v, i ) => {
        const x = ( i / ( data.length - 1 ) ) * w;
        const y = h - ( ( v - min ) / range ) * ( h - 4 ) - 2;
        return x + ',' + y;
    } ).join( ' ' );
    const color = positive ? '#00e5a0' : '#ff5577';
    const fillPoints = `0,${h} ${points} ${w},${h}`;
    return React.createElement( 'svg',
        { width: w, height: h, viewBox: `0 0 ${w} ${h}`, style: { display: 'block' } },
        React.createElement( 'defs', null,
            React.createElement( 'linearGradient', { id: `grad-${positive}`, x1: '0', y1: '0', x2: '0', y2: '1' },
                React.createElement( 'stop', { offset: '0%',   stopColor: color, stopOpacity: '0.2' } ),
                React.createElement( 'stop', { offset: '100%', stopColor: color, stopOpacity: '0'   } )
            )
        ),
        React.createElement( 'polygon', { points: fillPoints, fill: `url(#grad-${positive})` } ),
        React.createElement( 'polyline', { points, fill: 'none', stroke: color, strokeWidth: '1.5', strokeLinecap: 'round', strokeLinejoin: 'round' } )
    );
}

/* ── Premium Coin Row — matches reference design ── */
function truncateName( name, max ) {
    if ( !name ) return '';
    if ( name.length <= max ) return name;
    return name.slice( 0, max - 1 ) + '…';
}

function CoinRow( { coin, rank, onClick, livePrices, favorites, toggleFavorite } ) {
    const livePrice = livePrices[ coin.id ];
    const price     = livePrice || coin.current_price;
    const change24  = coin.price_change_percentage_24h;
    const positive  = change24 >= 0;
    const sparkData = coin.sparkline_in_7d?.price || [];
    const isMobile  = window.innerWidth <= 768;
    const buyLink   = getBuyLink();
    const buyLabel  = getBuyLabel();

    /* ── MOBILE ROW — clean compact list ── */
    if ( isMobile ) {
        return React.createElement( 'div', {
            onClick: () => onClick( coin ),
            style: { display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', gap: '10px' },
        },
            React.createElement( 'span', { style: { fontSize: '12px', color: '#555', fontWeight: '700', minWidth: '24px', textAlign: 'right', flexShrink: 0 } }, rank ),
            React.createElement( 'img', { src: coin.image, alt: coin.name, style: { width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0 }, onError: e => { e.target.style.display = 'none'; } } ),
            React.createElement( 'div', { style: { flex: 1, minWidth: 0 } },
                React.createElement( 'div', { style: { fontSize: '14px', fontWeight: '700', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, coin.name ),
                React.createElement( 'div', { style: { fontSize: '11px', color: '#555', marginTop: '2px' } }, formatLarge( coin.market_cap ) )
            ),
            React.createElement( SparklineChart, { data: sparkData.slice( -20 ), positive } ),
            React.createElement( 'div', { style: { textAlign: 'right', flexShrink: 0, minWidth: '80px' } },
                React.createElement( 'div', { style: { fontSize: '14px', fontWeight: '700', color: '#fff' } }, formatPrice( price ) ),
                React.createElement( 'div', { style: { fontSize: '12px', fontWeight: '700', color: positive ? '#00e5a0' : '#ff5577', marginTop: '2px' } }, ( positive ? '+' : '' ) + ( change24 || 0 ).toFixed( 2 ) + '%' )
            )
        );
    }

    /* ── DESKTOP ROW — compact with 1h/24h/7d ── */
    const change1h  = coin.price_change_percentage_1h_in_currency || 0;
    const change24h = coin.price_change_percentage_24h_in_currency || coin.price_change_percentage_24h || 0;
    const change7d  = coin.price_change_percentage_7d_in_currency || 0;
    const sym = coin.symbol.toUpperCase();
    const upDownClass = positive ? 'up' : 'down';

    const renderPct = ( v ) => {
        const cls = v >= 0 ? 'up' : 'down';
        const arrow = v >= 0 ? '↑' : '↓';
        return React.createElement( 'span', { className: 'vm-chg-text ' + cls },
            React.createElement( 'span', { className: 'arrow' }, arrow ),
            ' ' + Math.abs( v ).toFixed( 2 ) + '%'
        );
    };

    const isFav = favorites.includes( coin.id );

    return React.createElement( 'tr', { onClick: () => onClick( coin ) },
        React.createElement( 'td', { className: 'vm-td star',
            onClick: ( e ) => { e.stopPropagation(); toggleFavorite( coin.id ); },
            style: { cursor: 'pointer' }
        }, isFav ? React.createElement( 'span', { style: { color: '#fbbf24' } }, '★' ) : React.createElement( 'span', { style: { color: '#666' } }, '☆' ) ),
        React.createElement( 'td', { className: 'vm-td vm-rank l', style: { textAlign: 'left' } }, rank ),
        React.createElement( 'td', { className: 'vm-td l' },
            React.createElement( 'img', { src: coin.image, alt: coin.name, className: 'vm-coin-icon', style: { marginRight: '8px' }, onError: e => { e.target.style.display = 'none'; } } ),
            React.createElement( 'span', { className: 'vm-coin-name', title: coin.name }, truncateName( coin.name, 16 ) ),
            React.createElement( 'span', { className: 'vm-coin-symbol' }, '(' + sym + ')' )
        ),
        React.createElement( 'td', { className: 'vm-td num' },
            React.createElement( 'span', { className: 'vm-price-text ' + upDownClass }, formatPrice( price ) )
        ),
        React.createElement( 'td', { className: 'vm-td num' }, renderPct( change1h ) ),
        React.createElement( 'td', { className: 'vm-td num' }, renderPct( change24h ) ),
        React.createElement( 'td', { className: 'vm-td num' }, renderPct( change7d ) ),
        React.createElement( 'td', { className: 'vm-td num' },
            React.createElement( 'span', { className: 'vm-mcap ' + upDownClass }, formatLarge( coin.market_cap ) )
        ),
        React.createElement( 'td', { className: 'vm-td num' }, formatLarge( coin.total_volume ) ),
        React.createElement( 'td', { className: 'vm-td vm-spark-cell' },
            React.createElement( SparklineChart, { data: sparkData.slice( -20 ), positive } )
        ),
        React.createElement( 'td', { className: 'vm-td' },
            buyLink ? React.createElement( 'a', {
                href: buyLink, target: '_blank', rel: 'noopener noreferrer',
                onClick: e => e.stopPropagation(),
                className: 'vm-buy-btn',
            }, buyLabel ) : '-'
        )
    );
}
function ListedTokenRow( { token, index, onClick } ) {
    const change = parseFloat( token.change24h ) || 0;
    const pos    = change >= 0;
    const sparkData = [];
    const isMobileLT = window.innerWidth <= 768;
    if ( !isMobileLT ) {
        /* Desktop: render as table row */
        const tdS = { padding: '14px 12px', verticalAlign: 'middle', borderBottom: 'none', whiteSpace: 'nowrap' };
        const upDownClass = pos ? 'up' : 'down';
        const arrow = pos ? '↑' : '↓';
        return React.createElement( 'tr', { onClick: () => onClick( token ) },
            React.createElement( 'td', { className: 'vm-td star', style: { color: '#fbbf24', fontSize: '16px' } }, '★' ),
            React.createElement( 'td', { className: 'vm-td vm-rank l', style: { textAlign: 'left' } }, '—' ),
            React.createElement( 'td', { className: 'vm-td l' },
                token.logo
                    ? React.createElement( 'img', { src: token.logo, alt: token.name, className: 'vm-coin-icon', style: { marginRight: '8px' }, onError: e => { e.target.style.display = 'none'; } } )
                    : React.createElement( 'div', { className: 'vm-coin-icon', style: { background: 'linear-gradient(135deg,#7C63FF,#A855F7)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#fff', fontSize: '11px', marginRight: '8px' } }, token.symbol?.charAt(0) || '?' ),
                React.createElement( 'span', { className: 'vm-coin-name', title: token.name }, truncateName( token.name, 16 ) ),
                React.createElement( 'span', { className: 'vm-coin-symbol' }, '(' + ( token.symbol || '' ).toUpperCase() + ')' ),
                React.createElement( 'span', { className: token.featured ? 'vm-badge featured' : 'vm-badge listed', style: { marginLeft: '8px' } }, token.featured ? '⭐ FEATURED' : 'LISTED' )
            ),
            React.createElement( 'td', { className: 'vm-td num' },
                React.createElement( 'span', { className: 'vm-price-text ' + upDownClass }, token.price || '-' )
            ),
            React.createElement( 'td', { className: 'vm-td num' }, '-' ),
            React.createElement( 'td', { className: 'vm-td num' },
                React.createElement( 'span', { className: 'vm-chg-text ' + upDownClass },
                    React.createElement( 'span', { className: 'arrow' }, arrow ),
                    ' ' + Math.abs( change ).toFixed( 2 ) + '%'
                )
            ),
            React.createElement( 'td', { className: 'vm-td num' }, '-' ),
            React.createElement( 'td', { className: 'vm-td num' },
                React.createElement( 'span', { className: 'vm-mcap ' + upDownClass }, token.marketCap || '-' )
            ),
            React.createElement( 'td', { className: 'vm-td num' }, token.volume || '-' ),
            React.createElement( 'td', { className: 'vm-td vm-spark-cell' } ),
            React.createElement( 'td', { className: 'vm-td' } )
        );
    }

    return React.createElement( 'div', {
        onClick: () => onClick( token ),
        style: {
            display: 'flex',
            alignItems: 'center',
            padding: '14px 4px',
            borderBottom: 'none',
            cursor: 'pointer',
            transition: 'background 0.15s',
            gap: '10px',
            background: token.featured ? 'rgba(124,99,255,0.04)' : 'transparent',
        },
        onMouseEnter: e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)',
        onMouseLeave: e => e.currentTarget.style.background = token.featured ? 'rgba(124,99,255,0.04)' : 'transparent',
    },
        /* Icon */
        token.logo
            ? React.createElement( 'img', { src: token.logo, alt: token.name, style: { width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0 }, onError: e => { e.target.style.display = 'none'; } } )
            : React.createElement( 'div', { style: { width: '40px', height: '40px', borderRadius: '50%', background: token.featured ? 'linear-gradient(135deg,#7C63FF,#A855F7)' : 'rgba(124,99,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#fff', fontSize: '14px', flexShrink: 0 } }, token.symbol?.charAt(0) || '?' ),
        /* Name + badge + change */
        React.createElement( 'div', { style: { flex: 1, minWidth: 0 } },
            React.createElement( 'div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' } },
                React.createElement( 'span', { style: { fontSize: '15px', fontWeight: '700', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, token.name ),
                token.featured
                    ? React.createElement( 'span', { style: { fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: 'linear-gradient(135deg,#7C63FF,#A855F7)', color: '#fff', flexShrink: 0 } }, '⭐ FEATURED' )
                    : React.createElement( 'span', { style: { fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: 'rgba(124,99,255,0.2)', color: '#a78bfa', flexShrink: 0 } }, 'LISTED' )
            ),
            React.createElement( 'div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                React.createElement( 'span', { style: { fontSize: '12px', color: '#555', textTransform: 'uppercase', fontWeight: '600' } }, token.symbol ),
                React.createElement( 'span', { style: { fontSize: '12px', fontWeight: '700', color: pos ? '#00e5a0' : '#ff5577' } },
                    ( pos ? '▲ +' : '▼ ' ) + Math.abs( change ).toFixed( 2 ) + '%'
                )
            )
        ),
        /* Sparkline placeholder */
        React.createElement( 'div', { style: { width: '80px', height: '36px', flexShrink: 0 } } ),
        /* Price + MCap */
        React.createElement( 'div', { style: { textAlign: 'right', flexShrink: 0, minWidth: '90px' } },
            React.createElement( 'div', { style: { fontSize: '15px', fontWeight: '800', color: '#fff' } }, token.price || '-' ),
            React.createElement( 'div', { style: { fontSize: '11px', color: '#444', marginTop: '3px' } }, token.marketCap ? 'MCap ' + token.marketCap : '' )
        ),
        /* Star */
        React.createElement( 'button', { onClick: e => e.stopPropagation(), style: { background: 'none', border: 'none', color: '#333', fontSize: '18px', cursor: 'pointer', flexShrink: 0, padding: '4px' } }, '☆' )
    );

}

/* ── Listed Token Detail Modal ── */
function ListedTokenModal( { token, onClose } ) {
    if ( !token ) return null;
    const change = parseFloat( token.change24h ) || 0;
    const pos    = change >= 0;
    return React.createElement( 'div', { className: 'cv-modal-overlay', onClick: onClose },
        React.createElement( 'div', { className: 'cv-modal', onClick: e => e.stopPropagation(), style: { maxWidth: '520px', width: '90%' } },
            React.createElement( 'div', { className: 'cv-modal-header' },
                React.createElement( 'div', { style: { display: 'flex', alignItems: 'center', gap: '12px' } },
                    token.logo && React.createElement( 'img', { src: token.logo, alt: token.name, style: { width: '36px', height: '36px', borderRadius: '50%' } } ),
                    React.createElement( 'div', null,
                        React.createElement( 'h3', { style: { margin: 0, color: '#fff' } }, token.name ),
                        React.createElement( 'span', { style: { color: '#888', fontSize: '13px' } }, token.symbol + ' · ' + token.network )
                    )
                ),
                React.createElement( 'button', { className: 'cv-modal-close', onClick: onClose }, '✕' )
            ),
            React.createElement( 'div', { style: { padding: '20px' } },
                /* Badge */
                React.createElement( 'div', { style: { textAlign: 'center', marginBottom: '20px' } },
                    token.featured
                        ? React.createElement( 'span', { style: { fontSize: '12px', padding: '4px 12px', borderRadius: '20px', background: '#FC72FF', color: '#fff', fontWeight: '700' } }, '⭐ Featured Listing' )
                        : React.createElement( 'span', { style: { fontSize: '12px', padding: '4px 12px', borderRadius: '20px', background: 'rgba(124,111,255,0.2)', color: '#7C6FFF', fontWeight: '700' } }, '🔵 Listed Token' )
                ),
                /* Price */
                React.createElement( 'div', { style: { textAlign: 'center', marginBottom: '20px', padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px' } },
                    React.createElement( 'div', { style: { fontSize: '32px', fontWeight: '800', color: '#fff' } }, token.price || '-' ),
                    React.createElement( 'div', { style: { fontSize: '14px', color: pos ? '#00e5a0' : '#ff5577', marginTop: '4px', fontWeight: '600' } },
                        ( pos ? '▲ +' : '▼ ' ) + Math.abs( change ).toFixed( 2 ) + '% (24h)'
                    )
                ),
                /* Stats */
                React.createElement( 'div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' } },
                    [
                        { label: 'Market Cap', value: token.marketCap  || '-' },
                        { label: '24h Volume', value: token.volume     || '-' },
                        { label: 'Network',    value: token.network    || '-' },
                        { label: 'Contract',   value: token.contract ? token.contract.slice(0,12) + '...' : '-' },
                    ].map( ( s, i ) =>
                        React.createElement( 'div', { key: i, style: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '12px' } },
                            React.createElement( 'div', { style: { fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase' } }, s.label ),
                            React.createElement( 'div', { style: { fontSize: '13px', fontWeight: '600', color: '#fff' } }, s.value )
                        )
                    )
                ),
                /* Description */
                token.description && React.createElement( 'p', { style: { fontSize: '13px', color: '#aaa', lineHeight: '1.6', marginBottom: '16px' } }, token.description ),
                /* Website button */
                token.website && React.createElement( 'a', {
                    href: token.website, style: { display: 'block', textAlign: 'center', textDecoration: 'none', padding: '12px', borderRadius: '10px', background: '#FC72FF', color: '#fff', fontWeight: '700', fontSize: '14px' }
                }, 'Visit Project Website ↗' )
            )
        )
    );
}

/* ── Live Ticker Bar ── */

/* ── Scrolling Ticker Bar ── */
function ScrollingTicker( { coins, livePrices, onCoinClick } ) {
    if ( !coins || coins.length === 0 ) return null;
    /* duplicate items for seamless loop */
    const items = [ ...coins, ...coins ];
    return React.createElement( 'div', { className: 'vm-ticker-wrap' },
        React.createElement( 'div', { className: 'vm-ticker-track' },
            items.map( ( coin, i ) => {
                const price  = livePrices[ coin.id ] !== undefined ? livePrices[ coin.id ] : coin.current_price;
                const change = coin.price_change_percentage_24h || 0;
                const pos    = change >= 0;
                return React.createElement( 'div', {
                    key: i,
                    className: 'vm-ticker-item',
                    onClick: () => onCoinClick( coin ),
                },
                    coin.image && React.createElement( 'img', { src: coin.image, alt: coin.symbol } ),
                    React.createElement( 'span', { className: 'vm-ticker-symbol' }, coin.symbol.toUpperCase() ),
                    React.createElement( 'span', { className: 'vm-ticker-price' }, formatPrice( price ) ),
                    React.createElement( 'span', { className: 'vm-ticker-chg ' + ( pos ? 'up' : 'down' ) },
                        ( pos ? '+' : '' ) + change.toFixed(2) + '%'
                    )
                );
            } )
        )
    );
}

/* ── Price Cards ── */
function PriceCards( { coins, livePrices, onCoinClick } ) {
    if ( !coins || coins.length === 0 ) return null;
    const CARD_IDS = ['bitcoin','ethereum','solana','binancecoin'];
    const cardCoins = CARD_IDS.map( id => coins.find( c => c.id === id ) ).filter( Boolean );
    const finalCards = cardCoins.length >= 2 ? cardCoins : coins.slice(0,4);
    return React.createElement( 'div', { className: 'vm-price-cards' },
        finalCards.map( ( coin, i ) => {
            const price  = livePrices[ coin.id ] !== undefined ? livePrices[ coin.id ] : coin.current_price;
            const change = coin.price_change_percentage_24h || 0;
            const pos    = change >= 0;
            const color  = pos ? '#00e5a0' : '#ff5577';
            return React.createElement( 'div', {
                key: i,
                className: 'vm-price-card',
                style: { borderBottom: '3px solid ' + color },
                onClick: () => onCoinClick( coin ),
            },
                React.createElement( 'div', { className: 'vm-price-card-top' },
                    React.createElement( 'div', { className: 'vm-price-card-coin' },
                        coin.image && React.createElement( 'img', { src: coin.image, alt: coin.symbol } ),
                        React.createElement( 'span', { className: 'vm-price-card-symbol' }, coin.symbol.toUpperCase() + ' - USD' )
                    ),
                    React.createElement( 'span', { className: 'vm-price-card-chg ' + ( pos ? 'up' : 'down' ) },
                        ( pos ? '+' : '' ) + change.toFixed(2) + '%'
                    )
                ),
                React.createElement( 'div', { className: 'vm-price-card-vol' }, 'Vol: ' + formatLarge( coin.total_volume ) ),
                React.createElement( 'div', { className: 'vm-price-card-price' }, formatPrice( price ) ),
                React.createElement( 'div', { className: 'vm-price-card-bar', style: { background: color + '33' } },
                    React.createElement( 'div', { style: { height: '100%', width: Math.min(100, Math.abs(change) * 10) + '%', background: color, borderRadius: '2px' } } )
                )
            );
        } )
    );
}

/* Legacy TickerBar kept for backward compat */
function TickerBar( { coins, livePrices } ) { return null; }

function LargeSparkline( { data, positive } ) {
    if ( !data || data.length < 2 ) return React.createElement( 'div', { style: { height: '64px' } } );
    const min   = Math.min( ...data );
    const max   = Math.max( ...data );
    const range = max - min || 1;
    const w = 200; const h = 64;
    const pts = data.map( ( v, i ) => {
        const x = ( i / ( data.length - 1 ) ) * w;
        const y = h - ( ( v - min ) / range ) * ( h - 6 ) - 3;
        return x + ',' + y;
    } ).join( ' ' );
    const color    = positive ? '#00e5a0' : '#ff5577';
    const fillPts  = `0,${h} ${pts} ${w},${h}`;
    return React.createElement( 'svg', {
        width: '100%', height: h, viewBox: `0 0 ${w} ${h}`,
        preserveAspectRatio: 'none', style: { display: 'block' }
    },
        React.createElement( 'defs', null,
            React.createElement( 'linearGradient', { id: `lg-${positive}`, x1:'0', y1:'0', x2:'0', y2:'1' },
                React.createElement( 'stop', { offset: '0%',   stopColor: color, stopOpacity: '0.3' } ),
                React.createElement( 'stop', { offset: '100%', stopColor: color, stopOpacity: '0.02' } )
            )
        ),
        React.createElement( 'polygon', { points: fillPts, fill: `url(#lg-${positive})` } ),
        React.createElement( 'polyline', { points: pts, fill: 'none', stroke: color, strokeWidth: '1.5', strokeLinecap: 'round', strokeLinejoin: 'round' } )
    );
}

/* ── Price Cell with flash ── */
function PriceCell( { coinId, initialPrice, livePrices } ) {
    const livePrice = livePrices[ coinId ];
    const price     = livePrice !== undefined ? livePrice : initialPrice;
    const [ flash, setFlash ] = useState( null );
    const prevRef = useRef( initialPrice );

    useEffect( () => {
        if ( livePrice === undefined ) return;
        if ( prevRef.current !== undefined && livePrice !== prevRef.current ) {
            setFlash( livePrice > prevRef.current ? 'up' : 'down' );
            setTimeout( () => setFlash( null ), 800 );
        }
        prevRef.current = livePrice;
    }, [ livePrice ] );

    return React.createElement( 'td', { style: {
        fontWeight: '700', fontSize: '14px', padding: '14px 16px',
        color: flash === 'up' ? '#16a34a' : flash === 'down' ? '#dc2626' : '#1a1a2e',
        transition: 'color 0.4s',
        background: flash === 'up' ? 'rgba(22,163,74,0.08)' : flash === 'down' ? 'rgba(220,38,38,0.08)' : 'transparent',
    } }, formatPrice( price ) );
}

/* ── Coin Row ── */
/* ── Coin Detail Modal ── */
function CoinDetailModal( { coin, onClose, livePrices } ) {
    if ( !coin ) return null;
    const change    = coin.price_change_percentage_24h || 0;
    const pos       = change >= 0;
    const livePrice = livePrices[ coin.id ];
    const price     = livePrice !== undefined ? livePrice : coin.current_price;

    const stats = [
        { label: 'Market Cap',    value: formatLarge( coin.market_cap )        },
        { label: '24h Volume',    value: formatLarge( coin.total_volume )       },
        { label: '24h High',      value: formatPrice( coin.high_24h )           },
        { label: '24h Low',       value: formatPrice( coin.low_24h )            },
        { label: 'All Time High', value: formatPrice( coin.ath )                },
        { label: 'Circulating',   value: formatLarge( coin.circulating_supply ) },
    ];

    return React.createElement( 'div', { className: 'cv-modal-overlay', onClick: onClose },
        React.createElement( 'div', { className: 'cv-modal', onClick: e => e.stopPropagation(), style: { maxWidth: '520px', width: '90%' } },
            React.createElement( 'div', { className: 'cv-modal-header' },
                React.createElement( 'div', { style: { display: 'flex', alignItems: 'center', gap: '12px' } },
                    React.createElement( 'img', { src: coin.image, alt: coin.name, style: { width: '36px', height: '36px', borderRadius: '50%' } } ),
                    React.createElement( 'div', null,
                        React.createElement( 'h3', { style: { margin: 0, color: '#fff' } }, coin.name ),
                        React.createElement( 'span', { style: { color: '#888', fontSize: '13px' } }, coin.symbol.toUpperCase() + ' · Rank #' + coin.market_cap_rank )
                    )
                ),
                React.createElement( 'button', { className: 'cv-modal-close', onClick: onClose }, '✕' )
            ),
            React.createElement( 'div', { style: { padding: '20px' } },
                React.createElement( 'div', { style: { textAlign: 'center', marginBottom: '24px', padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px' } },
                    React.createElement( 'div', { style: { fontSize: '36px', fontWeight: '800', color: '#fff', letterSpacing: '-1px' } }, formatPrice( price ) ),
                    React.createElement( 'div', { style: { fontSize: '16px', color: pos ? '#00e5a0' : '#ff5577', marginTop: '6px', fontWeight: '600' } },
                        ( pos ? '▲ +' : '▼ ' ) + Math.abs( change ).toFixed( 2 ) + '% (24h)'
                    )
                ),
                React.createElement( 'div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' } },
                    stats.map( ( s, i ) =>
                        React.createElement( 'div', { key: i, style: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '12px' } },
                            React.createElement( 'div', { style: { fontSize: '11px', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' } }, s.label ),
                            React.createElement( 'div', { style: { fontSize: '14px', fontWeight: '700', color: '#fff' } }, s.value )
                        )
                    )
                ),
                React.createElement( 'a', {
                    href: '#/market/coin/' + coin.id, /* internal coin detail */
                    target: '_blank', rel: 'noopener noreferrer',
                    style: { display: 'block', textAlign: 'center', textDecoration: 'none', padding: '12px', borderRadius: '10px', background: '#FC72FF', color: '#fff', fontWeight: '700', fontSize: '14px' }
                }, 'View Details ↗' )
            )
        )
    );
}



/* ── Forum Tab — Crypto News Discussion ── */
function ForumTab() {
    const [ posts,   setPosts   ] = useState( [] );
    const [ loading, setLoading ] = useState( true );
    const [ filter,  setFilter  ] = useState( 'ALL' );

    const FILTERS = [
        { id: 'ALL',      label: 'All',      feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss&count=20' },
        { id: 'BTC',      label: 'Bitcoin',  feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/bitcoin&count=20' },
        { id: 'ETH',      label: 'Ethereum', feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/ethereum&count=20' },
        { id: 'SOL',      label: 'Solana',   feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/solana&count=20' },
        { id: 'DEFI',     label: 'DeFi',     feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/defi&count=20' },
        { id: 'NFT',      label: 'NFT',      feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/nft&count=20' },
        { id: 'MARKET',   label: 'Markets',  feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/markets&count=20' },
    ];

    const timeAgo = ( dateStr ) => {
        const diff = Math.floor( ( Date.now() - new Date(dateStr).getTime() ) / 1000 );
        if ( diff < 60    ) return diff + 's ago';
        if ( diff < 3600  ) return Math.floor( diff / 60 ) + 'm ago';
        if ( diff < 86400 ) return Math.floor( diff / 3600 ) + 'h ago';
        return Math.floor( diff / 86400 ) + 'd ago';
    };

    const fetchPosts = useCallback( async () => {
        setLoading( true );
        try {
            const active = FILTERS.find( f => f.id === filter ) || FILTERS[0];
            const res    = await fetch( active.feed );
            const data   = await res.json();
            if ( data?.items ) {
                setPosts( data.items.slice(0,20) );
            } else {
                setPosts( [] );
            }
        } catch ( err ) {
            setPosts( [] );
        } finally {
            setLoading( false );
        }
    }, [ filter ] );

    useEffect( () => { fetchPosts(); }, [ fetchPosts ] );

    const formatNum = ( n ) => {
        if ( n >= 1000 ) return ( n / 1000 ).toFixed( 1 ) + 'k';
        return n;
    };

    return React.createElement( 'div', null,

        /* Category filters */
        React.createElement( 'div', { style: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' } },
            FILTERS.map( f =>
                React.createElement( 'button', {
                    key: f.id,
                    onClick: () => setFilter( f.id ),
                    style: {
                        padding: '5px 14px', borderRadius: '20px', border: 'none',
                        cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit',
                        background: filter === f.id ? '#FC72FF' : 'rgba(255,255,255,0.06)',
                        color:      filter === f.id ? '#fff'    : '#aaaaaa',
                    }
                }, f.label )
            )
        ),

        /* Posts */
        loading
            ? React.createElement( 'div', { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px', color: '#888', gap: '14px' } },
                React.createElement( 'div', { className: 'cv-market-spinner' } ),
                React.createElement( 'p', null, 'Loading community posts...' )
              )
            : posts.length === 0
                ? React.createElement( 'div', { style: { textAlign: 'center', padding: '40px', color: '#888' } },
                    React.createElement( 'p', null, 'No posts available. Try another subreddit.' )
                  )
                : React.createElement( 'div', { className: 'cv-news-grid', style: { display: 'grid', gridTemplateColumns: window.innerWidth > 1024 ? 'repeat(4, 1fr)' : window.innerWidth > 640 ? 'repeat(2, 1fr)' : '1fr', gap: '16px', marginTop: '8px', width: '100%' } },
                    posts.map( ( post, i ) =>
                        React.createElement( 'a', {
                            key: i,
                            href: 'https://reddit.com' + post.permalink,
                            target: '_blank', rel: 'noopener noreferrer',
                            style: { textDecoration: 'none', display: 'block' }
                        },
                            React.createElement( 'div', {
                                style: {
                                    display: 'flex', gap: '14px', padding: '14px 0',
                                    borderBottom: 'none',
                                },
                                onMouseEnter: e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)',
                                onMouseLeave: e => e.currentTarget.style.background = 'transparent',
                            },
                                /* Vote score */
                                React.createElement( 'div', { style: {
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    gap: '2px', minWidth: '40px', flexShrink: 0,
                                } },
                                    React.createElement( 'span', { style: { fontSize: '16px', color: '#FC72FF' } }, '▲' ),
                                    React.createElement( 'span', { style: { fontSize: '12px', fontWeight: '700', color: '#fff' } }, formatNum( post.score ) )
                                ),
                                /* Thumbnail */
                                post.thumbnail && post.thumbnail.startsWith( 'http' ) &&
                                React.createElement( 'img', {
                                    src: post.thumbnail, alt: post.title,
                                    style: { width: '80px', height: '60px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0, background: '#222' },
                                    onError: e => { e.target.style.display = 'none'; }
                                } ),
                                /* Content */
                                React.createElement( 'div', { style: { flex: 1 } },
                                    /* Author + time + flair */
                                    React.createElement( 'div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' } },
                                        React.createElement( 'span', { style: { fontSize: '12px', fontWeight: '700', color: '#FC72FF' } }, 'u/' + post.author ),
                                        React.createElement( 'span', { style: { fontSize: '11px', color: '#666' } }, timeAgo( post.created_utc ) ),
                                        post.link_flair_text && React.createElement( 'span', { style: {
                                            fontSize: '10px', padding: '1px 8px', borderRadius: '4px',
                                            background: 'rgba(252,114,255,0.1)', color: '#FC72FF', fontWeight: '600',
                                        } }, post.link_flair_text )
                                    ),
                                    /* Title */
                                    React.createElement( 'div', { style: { fontSize: '14px', fontWeight: '700', color: '#fff', lineHeight: '1.4', marginBottom: '6px' } },
                                        post.title
                                    ),
                                    /* Stats */
                                    React.createElement( 'div', { style: { display: 'flex', gap: '16px', fontSize: '12px', color: '#666' } },
                                        React.createElement( 'span', null, '💬 ' + formatNum( post.num_comments ) + ' comments' ),
                                        React.createElement( 'span', null, '👍 ' + Math.round( post.upvote_ratio * 100 ) + '% upvoted' ),
                                        React.createElement( 'span', null, 'r/' + post.subreddit )
                                    )
                                )
                            )
                        )
                    )
                ),

        /* View more on news source */
        !loading && posts.length > 0 && React.createElement( 'div', { style: { textAlign: 'center', padding: '20px 0' } },
            React.createElement( 'a', {
                href: 'https://cointelegraph.com',
                target: '_blank', rel: 'noopener noreferrer',
                style: { fontSize: '13px', color: '#FC72FF', fontWeight: '600', textDecoration: 'none' }
            }, 'View More on news source ▾' )
        )
    );
}

/* ── News Feed Component ── */
function NewsTab() {
    const navigate = useNavigate();
    const [ news,        setNews        ] = useState( [] );
    const [ loading,     setLoading     ] = useState( true );
    const [ adminPosts,  setAdminPosts  ] = useState( [] );
    const [ activeTag,   setActiveTag   ] = useState( 'ALL' );

    const NEWS_TAGS = [
        { id: 'ALL',        label: 'Latest',    feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss'                                    },
        { id: 'BTC',        label: 'Bitcoin',   feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/bitcoin'                        },
        { id: 'ETH',        label: 'Ethereum',  feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/ethereum'                       },
        { id: 'SOL',        label: 'Solana',    feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/solana'                         },
        { id: 'DEFI',       label: 'DeFi',      feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/defi'                           },
        { id: 'NFT',        label: 'NFT',       feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/nft'                            },
        { id: 'MARKET',     label: 'Markets',   feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/markets'                        },
        { id: 'REGULATION', label: 'Regulation',feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://cointelegraph.com/rss/tag/regulation'                     },
        { id: 'COINDESK',   label: 'news source',  feed: 'https://api.rss2json.com/v1/api.json?rss_url=https://www.coindesk.com/arc/outboundfeeds/rss/'                  },
    ];

    const timeAgo = ( timestamp ) => {
        const diff = Math.floor( ( Date.now() / 1000 ) - timestamp );
        if ( diff < 60   ) return diff + 's ago';
        if ( diff < 3600 ) return Math.floor( diff / 60 ) + 'm ago';
        if ( diff < 86400) return Math.floor( diff / 3600 ) + 'h ago';
        return Math.floor( diff / 86400 ) + 'd ago';
    };

    const fetchNews = useCallback( async () => {
        setLoading( true );
        try {
            const activeTagObj = NEWS_TAGS.find( t => t.id === activeTag ) || NEWS_TAGS[0];
            const res  = await fetch( activeTagObj.feed );
            const data = await res.json();
            if ( data.items && data.items.length > 0 ) {
                const sourceName = activeTag === 'COINDESK' ? 'news source' : 'news source';
                const normalized = data.items.slice( 0, 20 ).map( item => ({
                    title:        item.title,
                    url:          item.link,
                    imageurl:     item.enclosure?.link || item.thumbnail || '',
                    body:         item.description?.replace( /<[^>]+>/g, '' ) || '',
                    source:       item.author || sourceName,
                    source_info:  { name: sourceName },
                    published_on: Math.floor( new Date( item.pubDate ).getTime() / 1000 ),
                    categories:   activeTag + '|CRYPTOCURRENCY|MARKET',
                }) );
                setNews( normalized );
            } else {
                setNews( [] );
            }
        } catch ( err ) {
            setNews( [] );
        } finally {
            setLoading( false );
        }
    }, [ activeTag ] );

    // Fetch admin posts from WordPress REST API
    useEffect( () => {
        const _mkt      = window.chainvaultSettings || {};
        const _blogOn   = _mkt.marketBlogEnabled  !== false;
        const _blogCount= parseInt( _mkt.marketBlogCount    || 3 );
        const _blogCat  = parseInt( _mkt.marketBlogCategory || 0 );
        const _blogOrd  = _mkt.marketBlogOrder || 'desc';
        if ( !_blogOn ) { setAdminPosts([]); return; }
        const catParam = _blogCat > 0 ? '&categories=' + _blogCat : '';
        fetch( '/wp-json/wp/v2/posts?per_page=' + _blogCount + '&order=' + _blogOrd + '&_embed' + catParam )
            .then( r => r.json() )
            .then( data => {
                if ( Array.isArray( data ) ) setAdminPosts( data );
            } )
            .catch( () => {} );
    }, [] );

    useEffect( () => { fetchNews(); }, [ fetchNews ] );
    useEffect( () => {
        const t = setInterval( fetchNews, 5 * 60 * 1000 );
        return () => clearInterval( t );
    }, [ fetchNews ] );

    const getSentiment = ( article ) => {
        const text = ( article.title + ' ' + article.body ).toLowerCase();
        const pos  = [ 'surge', 'rally', 'bullish', 'gain', 'rise', 'pump', 'high', 'record', 'positive', 'growth' ];
        const neg  = [ 'crash', 'dump', 'bearish', 'fall', 'drop', 'loss', 'low', 'negative', 'decline', 'hack' ];
        const posCount = pos.filter( w => text.includes( w ) ).length;
        const negCount = neg.filter( w => text.includes( w ) ).length;
        if ( posCount > negCount ) return 'POSITIVE';
        if ( negCount > posCount ) return 'NEGATIVE';
        return 'NEUTRAL';
    };

    return React.createElement( 'div', null,

        /* Tag filters */
        React.createElement( 'div', { style: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' } },
            NEWS_TAGS.map( tag =>
                React.createElement( 'button', {
                    key: tag.id,
                    onClick: () => setActiveTag( tag.id ),
                    style: {
                        padding: '5px 14px', borderRadius: '20px', border: 'none',
                        cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit',
                        background: activeTag === tag.id ? '#FC72FF' : 'rgba(255,255,255,0.06)',
                        color:      activeTag === tag.id ? '#fff'    : '#aaaaaa',
                    }
                }, tag.label )
            )
        ),

        /* Admin Featured Posts */
        adminPosts.length > 0 && React.createElement( 'div', { style: { marginBottom: '16px' } },
            adminPosts.map( ( post, i ) =>
                React.createElement( 'a', {
                    key: i, href: post.link, target: '_blank', rel: 'noopener noreferrer',
                    style: { textDecoration: 'none', display: 'block' }
                },
                    React.createElement( 'div', { style: {
                        display: 'flex', gap: '16px', padding: '16px',
                        background: 'rgba(252,114,255,0.06)',
                        border: '1px solid rgba(252,114,255,0.2)',
                        borderRadius: '12px', marginBottom: '10px',
                        transition: 'border-color 0.2s',
                    } },
                        /* Featured thumbnail */
                        post._embedded?.['wp:featuredmedia']?.[0]?.source_url &&
                        React.createElement( 'img', {
                            src: post._embedded['wp:featuredmedia'][0].source_url,
                            alt: post.title.rendered,
                            style: { width: '120px', height: '80px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }
                        } ),
                        React.createElement( 'div', { style: { flex: 1 } },
                            React.createElement( 'div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' } },
                                React.createElement( 'span', { style: {
                                    fontSize: '10px', fontWeight: '700', padding: '2px 8px',
                                    borderRadius: '4px', background: '#FC72FF', color: '#fff',
                                } }, '⭐ FEATURED' ),
                                React.createElement( 'span', { style: { fontSize: '11px', color: '#888' } }, 'By Admin' )
                            ),
                            React.createElement( 'div', { style: { fontSize: '15px', fontWeight: '700', color: '#fff', marginBottom: '6px', lineHeight: '1.4' } },
                                post.title.rendered
                            ),
                            React.createElement( 'div', { style: { fontSize: '12px', color: '#aaa', lineHeight: '1.5' } },
                                post.excerpt?.rendered?.replace( /<[^>]+>/g, '' ).slice( 0, 120 ) + '...'
                            )
                        )
                    )
                )
            )
        ),

        /* Live News Feed */
        loading
            ? React.createElement( 'div', { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px', color: '#888', gap: '14px' } },
                React.createElement( 'div', { className: 'cv-market-spinner' } ),
                React.createElement( 'p', null, 'Loading latest crypto news...' )
              )
            : news.length === 0
                ? React.createElement( 'div', { style: { textAlign: 'center', padding: '40px', color: '#888' } },
                    React.createElement( 'p', null, 'No news available right now. Try again later.' )
                  )
                : React.createElement( 'div', { className: 'cv-news-grid', style: { display: 'grid', gridTemplateColumns: window.innerWidth > 1024 ? 'repeat(4, 1fr)' : window.innerWidth > 640 ? 'repeat(2, 1fr)' : '1fr', gap: '16px', marginTop: '8px', width: '100%' } },
                    news.map( ( article, i ) => {
                        const sentiment = getSentiment( article );
                        const sentColor = sentiment === 'POSITIVE' ? '#00e5a0' : sentiment === 'NEGATIVE' ? '#ff5577' : '#888';
                        const cats      = article.categories ? article.categories.split( '|' ).slice( 0, 4 ) : [];
                        return React.createElement( 'div', {
                            key: i,
                            onClick: () => {
                                // Build a stable id from article fields (RSS has no id)
                                const articleId = encodeURIComponent( ( article.title || '' ).slice( 0, 50 ).replace( /[^a-zA-Z0-9]/g, '-' ).toLowerCase() ) || ( 'article-' + i );
                                const articleWithId = Object.assign( {}, article, { _id: articleId, body: article.description || article.body || article.contentSnippet || '', imageurl: article.thumbnail || article.imageurl || article.image, source: article.author || article.source || 'news source', source_info: { name: article.author || 'news source' }, published_on: article.pubDate ? Math.floor( new Date( article.pubDate ).getTime() / 1000 ) : Math.floor( Date.now() / 1000 ), url: article.link || article.url } );
                                try { sessionStorage.setItem( 'cv_news_article_' + articleId, JSON.stringify( articleWithId ) ); } catch ( e ) {}
                                navigate( '/market/news/' + articleId );
                            },
                            style: { textDecoration: 'none', display: 'block', cursor: 'pointer' }
                        },
                            React.createElement( 'div', { style: {
                                display: 'flex', flexDirection: 'column', padding: '0',
                                background: 'rgba(255,255,255,0.02)', borderRadius: '12px', overflow: 'hidden',
                                height: '100%', cursor: 'pointer',
                                transition: 'transform 0.2s, background 0.2s',
                            },
                                onMouseEnter: e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; },
                                onMouseLeave: e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.transform = 'translateY(0)'; },
                            },
                                /* Thumbnail */
                                React.createElement( 'img', {
                                    src: article.imageurl, alt: article.title,
                                    style: { width: '100%', height: '160px', objectFit: 'cover', display: 'block', background: '#1a1a2e' },
                                    onError: e => { e.target.style.display = 'none'; }
                                } ),
                                /* Content */
                                React.createElement( 'div', { style: { padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 } },
                                    /* Source + time */
                                    React.createElement( 'div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                                        React.createElement( 'span', { style: { fontSize: '12px', fontWeight: '700', color: '#FC72FF' } }, article.source_info?.name || article.source ),
                                        React.createElement( 'span', { style: { fontSize: '11px', color: '#666' } }, timeAgo( article.published_on ) )
                                    ),
                                    /* Title */
                                    React.createElement( 'div', { style: { fontSize: '14px', fontWeight: '700', color: '#fff', lineHeight: '1.4' } },
                                        article.title
                                    ),
                                    /* Excerpt */
                                    React.createElement( 'div', { style: { fontSize: '12px', color: '#aaa', lineHeight: '1.5' } },
                                        article.body?.slice( 0, 120 ) + '...'
                                    ),
                                    /* Categories + Sentiment */
                                    React.createElement( 'div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' } },
                                        React.createElement( 'div', { style: { display: 'flex', gap: '4px', flexWrap: 'wrap' } },
                                            cats.map( ( cat, ci ) =>
                                                React.createElement( 'span', { key: ci, style: {
                                                    fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                                                    background: 'rgba(255,255,255,0.06)', color: '#888',
                                                } }, cat.trim() )
                                            )
                                        ),
                                        React.createElement( 'span', { style: {
                                            fontSize: '10px', fontWeight: '700', padding: '2px 8px',
                                            borderRadius: '4px', border: '1px solid ' + sentColor, color: sentColor,
                                        } }, 'Sentiment: ' + sentiment )
                                    )
                                )
                            )
                        );
                    } )
                ),

        /* View All News */
        !loading && news.length > 0 && React.createElement( 'div', { style: { textAlign: 'center', padding: '20px 0' } },
            React.createElement( 'a', {
                href: 'https://www.cryptocompare.com/news/', target: '_blank', rel: 'noopener noreferrer',
                style: { fontSize: '13px', color: '#FC72FF', fontWeight: '600', textDecoration: 'none' }
            }, 'View All News ▾' )
        )
    );
}

/* ── Main Page ── */
function VaultMarketPage() {
    const navigate = useNavigate();
    const [ activeMarketTab,    setActiveMarketTab    ] = useState( 'coins' );

    /* ── Favorites (localStorage) ── */
    const [ favorites, setFavorites ] = useState( () => {
        try {
            const raw = localStorage.getItem( 'cv_favorites' );
            return raw ? JSON.parse( raw ) : [];
        } catch ( e ) { return []; }
    } );
    const [ showFavoritesOnly, setShowFavoritesOnly ] = useState( false );

    const toggleFavorite = ( coinId ) => {
        setFavorites( prev => {
            const next = prev.includes( coinId )
                ? prev.filter( id => id !== coinId )
                : [ ...prev, coinId ];
            try { localStorage.setItem( 'cv_favorites', JSON.stringify( next ) ); } catch ( e ) {}
            return next;
        } );
    };
    const [ selectedListedToken, setSelectedListedToken ] = useState( null );
    const listedTokens = ( window.chainvaultSettings?.listedTokens || [] );
    const featuredTokens = listedTokens.filter( t => t.featured === true || t.featured === '1' || t.featured === 1 );
    const regularListedTokens = listedTokens.filter( t => !t.featured || t.featured === false || t.featured === '0' || t.featured === 0 );
    /* ── Market settings from admin ── */
    const mktSettings    = window.chainvaultSettings || {};
    const PER_PAGE       = Math.min( 250, parseInt( mktSettings.coinsPerPage || 50 ) );
    const DEFAULT_SORT   = mktSettings.marketSort     || 'market_cap_desc';
    const MARKET_CURR    = mktSettings.marketCurrency || 'usd';
    const REFRESH_MINS   = parseInt( mktSettings.marketRefresh || 3 );
    const TICKER_ON      = mktSettings.tickerEnabled     !== false;
    const NEWS_ON        = mktSettings.marketNewsEnabled !== false;
    const BLOG_ON        = mktSettings.marketBlogEnabled !== false;
    const BLOG_COUNT     = parseInt( mktSettings.marketBlogCount    || 3 );
    const BLOG_CATEGORY  = parseInt( mktSettings.marketBlogCategory || 0 );
    const BLOG_ORDER     = mktSettings.marketBlogOrder || 'desc';

    const [ coins,        setCoins        ] = useState( [] );
    const [ loading,      setLoading      ] = useState( true );
    const [ search,       setSearch       ] = useState( '' );
    const [ sortBy,       setSortBy       ] = useState( DEFAULT_SORT );
    const [ selectedCoin, setSelectedCoin ] = useState( null );
    const [ page,         setPage         ] = useState( 1 );
    const [ globalStats,  setGlobalStats  ] = useState( null );
    const [ lastUpdated,  setLastUpdated  ] = useState( null );
    const [ activeFilter, setActiveFilter ] = useState( 'all' );
    const [ livePrices,   setLivePrices   ] = useState( {} );
    const [ hasApiKey,    setHasApiKey    ] = useState( false );
    const [ isLive,       setIsLive       ] = useState( false );

    const coinIdsRef = useRef( [] );


    useEffect( () => { setHasApiKey( !!getSettings().coingeckoApiKey ); }, [] );

    const fetchCoins = useCallback( async () => {
        setLoading( true );
        try {
            let url = PROXY_URL + '/coins/markets?vs_currency=' + MARKET_CURR + '&order=' + sortBy + '&per_page=' + PER_PAGE + '&page=' + page + '&sparkline=true&price_change_percentage=1h%2C24h%2C7d';
            if ( activeFilter !== 'all' ) url += '&category=' + activeFilter;
            const res = await fetch( url, { headers: getCoinGeckoHeaders() } );
            if ( res.status === 429 ) {
                toast( 'Rate limited. Add your free crypto data API key in WordPress Admin → ChainVault → Advanced Settings.', { icon: '⚠️', duration: 6000 } );
                setLoading( false );
                return;
            }
            const data = await res.json();
            if ( Array.isArray( data ) ) {
                setCoins( data );
                coinIdsRef.current = data.map( c => c.id );
                setLastUpdated( new Date().toLocaleTimeString() );
            }
        } catch ( err ) {
            toast.error( 'Failed to load market data.' );
        } finally {
            setLoading( false );
        }
    }, [ sortBy, page, activeFilter ] );

    const fetchGlobalStats = useCallback( async () => {
        try {
            const res  = await fetch( PROXY_URL + '/global', { headers: getCoinGeckoHeaders() } );
            if ( res.status === 429 ) return;
            const data = await res.json();
            if ( data.data ) setGlobalStats( data.data );
        } catch ( err ) {}
    }, [] );

    const fetchLivePrices = useCallback( async () => {
        const ids = coinIdsRef.current;
        if ( !ids || ids.length === 0 ) return;
        try {
            const url  = COINGECKO_URL + '/simple/price?ids=' + ids.slice( 0, 50 ).join( ',' ) + '&vs_currencies=usd';
            const res  = await fetch( url, { headers: getCoinGeckoHeaders() } );
            if ( res.status === 429 ) return;
            const data = await res.json();
            if ( data && typeof data === 'object' ) {
                const updated = {};
                Object.keys( data ).forEach( id => { if ( data[ id ]?.usd ) updated[ id ] = data[ id ].usd; } );
                if ( Object.keys( updated ).length > 0 ) { setLivePrices( updated ); setIsLive( true ); }
            }
        } catch ( err ) {}
    }, [] );

    useEffect( () => { fetchCoins(); fetchGlobalStats(); }, [ fetchCoins, fetchGlobalStats ] );
    useEffect( () => { const t = setInterval( fetchCoins, REFRESH_MINS * 60 * 1000 ); return () => clearInterval( t ); }, [ fetchCoins ] );
    useEffect( () => { if ( coins.length === 0 ) return; const t = setInterval( fetchLivePrices, 10000 ); return () => clearInterval( t ); }, [ fetchLivePrices, coins ] );

    const filtered  = coins.filter( c => c.name.toLowerCase().includes( search.toLowerCase() ) || c.symbol.toLowerCase().includes( search.toLowerCase() ) );
    const finalCoins = showFavoritesOnly ? filtered.filter( c => favorites.includes( c.id ) ) : filtered;
    const totalMcap = globalStats ? formatLarge( globalStats.total_market_cap?.usd ) : '-';
    const totalVol  = globalStats ? formatLarge( globalStats.total_volume?.usd ) : '-';
    const btcDom    = globalStats?.market_cap_percentage?.btc ? globalStats.market_cap_percentage.btc.toFixed( 1 ) + '%' : '-';
    const activeCryp = globalStats?.active_cryptocurrencies ? globalStats.active_cryptocurrencies.toLocaleString() : '-';
    const settings  = getSettings();
    const adSlotTop = settings.marketAdTop  || '';
    const adSlotBot = settings.marketAdBottom || '';

    return React.createElement( 'div', { style: { background: '#0a0a12', minHeight: '100vh' } },

        React.createElement( 'div', { className: 'cv-page cv-vault-market vm-wrap', 'data-theme': 'dark' },

            adSlotTop && React.createElement( 'div', { dangerouslySetInnerHTML: { __html: adSlotTop } } ),

            /* Header */
            React.createElement( 'div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingTop: '8px' } },
                React.createElement( 'div', null,
                    React.createElement( 'h1', { style: { fontSize: '22px', fontWeight: '900', color: '#fff', margin: '0 0 4px 0', letterSpacing: '-0.5px' } }, 'Cryptoassets' ),
                    React.createElement( 'p', { style: { color: '#888', fontSize: '13px', margin: 0 } },
                        'Live cryptocurrency prices and market data',
                        lastUpdated && React.createElement( 'span', { style: { color: '#666', marginLeft: '8px' } }, '· Updated ' + lastUpdated ),
                        isLive && React.createElement( 'span', { style: { color: '#00e5a0', marginLeft: '8px', fontWeight: '600' } }, '● Live' )
                    )
                ),
                React.createElement( 'button', {
                    onClick: fetchCoins, disabled: loading,
                    style: { padding: '8px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }
                }, loading ? 'Loading...' : '⟳ Refresh' )
            ),

            /* Global Stats Bar */
            React.createElement( 'div', { style: {
                display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: '1px',
                background: 'rgba(255,255,255,0.04)', borderRadius: '16px', overflow: 'hidden', marginBottom: '20px',
                border: '1px solid rgba(255,255,255,0.06)',
            } },
                [
                    { label: 'Market Cap',    value: totalMcap,  color: '#fff'     },
                    { label: '24h Volume',    value: totalVol,   color: '#fff'     },
                    { label: 'BTC Dominance', value: btcDom,     color: '#F7931A'  },
                    { label: 'Active Cryptos',value: activeCryp, color: '#7C63FF'  },
                ].map( ( s, i ) =>
                    React.createElement( 'div', { key: i, style: { padding: '14px 16px', background: 'rgba(255,255,255,0.02)' } },
                        React.createElement( 'div', { style: { fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' } }, s.label ),
                        React.createElement( 'div', { style: { fontSize: '18px', fontWeight: '800', color: s.color } }, s.value )
                    )
                )
            ),

            /* Scrolling Ticker */
            isTickerEnabled() && coins.length > 0 && React.createElement( ScrollingTicker, {
                coins: coins.slice(0,20), livePrices, onCoinClick: c => navigate('/market/coin/' + c.id)
            }),

            /* Price Cards */
            isTickerEnabled() && coins.length > 0 && React.createElement( PriceCards, {
                coins: coins.slice(0,4), livePrices, onCoinClick: c => navigate('/market/coin/' + c.id)
            }),

            /* API Key notice */
            !hasApiKey && React.createElement( 'div', { style: {
                background: 'rgba(252,114,255,0.06)', border: '1px solid rgba(252,114,255,0.2)',
                borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#cccccc',
            } },
                '💡 Add your free crypto data API key in ',
                React.createElement( 'strong', { style: { color: '#FC72FF' } }, 'WordPress Admin → ChainVault → Advanced Settings' ),
                ' to unlock category filters. Free at ',
                React.createElement( 'a', { href: 'https://www.coingecko.com/en/api', target: '_blank', rel: 'noopener noreferrer', style: { color: '#FC72FF' } }, 'coingecko.com/api' )
            ),



            /* Search + Sort */
            React.createElement( 'div', { style: { display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'center' } },
                React.createElement( 'div', { style: { position: 'relative', flex: 1 } },
                    React.createElement( 'span', { style: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#666' } }, '🔍' ),
                    React.createElement( 'input', {
                        type: 'text', placeholder: 'Search coins...',
                        value: search, onChange: e => setSearch( e.target.value ),
                        style: { width: '100%', padding: '10px 36px', background: '#111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none', fontFamily: 'inherit' }
                    } ),
                    search && React.createElement( 'button', { onClick: () => setSearch( '' ), style: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#666', cursor: 'pointer' } }, '✕' )
                ),
                React.createElement( 'select', {
                    value: sortBy, onChange: e => { setSortBy( e.target.value ); setPage( 1 ); },
                    style: { padding: '10px 14px', background: '#111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', color: '#fff', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', minWidth: '180px' }
                },
                    React.createElement( 'option', { value: 'market_cap_desc' }, 'Market Cap High to Low' ),
                    React.createElement( 'option', { value: 'market_cap_asc'  }, 'Market Cap Low to High' ),
                    React.createElement( 'option', { value: 'volume_desc'     }, 'Volume High to Low'     ),
                    React.createElement( 'option', { value: 'price_desc'      }, 'Price High to Low'      ),
                    React.createElement( 'option', { value: 'price_asc'       }, 'Price Low to High'      )
                )
            ),

            /* Category Filters */
            React.createElement( 'div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' } },
                CATEGORY_FILTERS.map( f =>
                    React.createElement( 'button', {
                        key: f.id,
                        onClick: () => {
                            if ( f.id !== 'all' && !hasApiKey ) {
                                toast( 'Add your free crypto data API key to use category filters.', { icon: '🔑', duration: 4000 } );
                                return;
                            }
                            setActiveFilter( f.id ); setPage( 1 );
                        },
                        style: {
                            padding: '7px 16px', borderRadius: '20px', border: activeFilter === f.id ? 'none' : '1px solid rgba(255,255,255,0.08)', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit',
                            background: activeFilter === f.id ? 'linear-gradient(135deg,#7C63FF,#A855F7)' : 'rgba(255,255,255,0.04)',
                            color:      activeFilter === f.id ? '#fff' : '#888',
                            opacity:    f.id !== 'all' && !hasApiKey ? 0.5 : 1,
                            whiteSpace: 'nowrap',
                            boxShadow:  activeFilter === f.id ? '0 2px 10px rgba(124,99,255,0.3)' : 'none',
                        }
                    }, f.label + ( f.id !== 'all' && !hasApiKey ? ' 🔑' : '' ) )
                )
            ),

            React.createElement( AdSlot, { html: getAdSlot2() } ),

            /* COINS | FORUM Tabs */
            React.createElement( 'div', { style: { display: 'flex', borderBottom: 'none', marginBottom: '20px' } },
                [ { id: 'coins', label: 'COINS' } ].map( tab =>
                    React.createElement( 'button', {
                        key: tab.id,
                        onClick: () => setActiveMarketTab( tab.id ),
                        style: {
                            padding: '12px 24px', background: 'none', border: 'none',
                            borderBottom: activeMarketTab === tab.id ? '2px solid #FC72FF' : '2px solid transparent',
                            color: activeMarketTab === tab.id ? '#FC72FF' : '#888',
                            fontWeight: '700', fontSize: '14px', cursor: 'pointer',
                            fontFamily: 'inherit', letterSpacing: '0.5px',
                            marginBottom: '-1px',
                        }
                    }, tab.label )
                )
            ),



            /* COINS Tab Content — Desktop table / Mobile list */
            activeMarketTab === 'coins' && React.createElement( 'div', { style: { marginBottom: '20px' } },
                loading && coins.length === 0
                    ? React.createElement( 'div', { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', color: '#555', gap: '16px' } },
                        React.createElement( 'div', { className: 'cv-market-spinner' } ),
                        React.createElement( 'p', { style: { color: '#555' } }, 'Loading market data...' )
                      )
                    : filtered.length === 0
                        ? React.createElement( 'div', { style: { textAlign: 'center', padding: '60px', color: '#555' } },
                            React.createElement( 'p', null, search ? 'No coins found for "' + search + '"' : 'No data. Click Refresh.' )
                          )
                        : window.innerWidth <= 768
                            /* ── MOBILE: clean list ── */
                            ? React.createElement( 'div', { style: { display: 'flex', flexDirection: 'column' } },
                                featuredTokens.map( ( token, i ) => React.createElement( ListedTokenRow, { key: 'featured-' + i, token, index: i, onClick: setSelectedListedToken } ) ),
                                regularListedTokens.map( ( token, i ) => React.createElement( ListedTokenRow, { key: 'listed-' + i, token, index: i, onClick: setSelectedListedToken } ) ),
                                finalCoins.map( ( coin, index ) => React.createElement( CoinRow, { key: coin.id, coin, rank: ( page - 1 ) * PER_PAGE + index + 1, onClick: ( c ) => navigate( '/market/coin/' + c.id ), livePrices, favorites, toggleFavorite } ) )
                              )
                            /* ── DESKTOP: full market style table ── */
                            : React.createElement( 'div', { className: 'vm-card' },
                                React.createElement( 'table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'auto' } },
                                    React.createElement( 'thead', null,
                                        React.createElement( 'tr', null,
                                            [ '', '#', 'Name', 'Price', '1h %', '24h %', '7d %', 'Market Cap', 'Volume 24H', 'Graph (7D)', 'Buy' ].map( ( h, i ) =>
                                                React.createElement( 'th', { key: i, className: i === 2 ? 'vm-th l' : 'vm-th' }, h )
                                            )
                                        )
                                    ),
                                    React.createElement( 'tbody', null,
                                        featuredTokens.map( ( token, i ) => React.createElement( ListedTokenRow, { key: 'featured-' + i, token, index: i, onClick: setSelectedListedToken } ) ),
                                        regularListedTokens.map( ( token, i ) => React.createElement( ListedTokenRow, { key: 'listed-' + i, token, index: i, onClick: setSelectedListedToken } ) ),
                                        finalCoins.map( ( coin, index ) => React.createElement( CoinRow, { key: coin.id, coin, rank: ( page - 1 ) * PER_PAGE + index + 1, onClick: ( c ) => navigate( '/market/coin/' + c.id ), livePrices, favorites, toggleFavorite } ) )
                                    )
                                )
                              )
            ),

            /* Pagination — only show for coins tab */
            activeMarketTab === 'coins' && !search && React.createElement( 'div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '16px 0' } },
                React.createElement( 'button', {
                    onClick: () => setPage( p => Math.max( 1, p - 1 ) ), disabled: page === 1 || loading,
                    style: { padding: '8px 20px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' }
                }, '← Previous' ),
                React.createElement( 'span', { style: { color: '#888', fontSize: '14px' } }, 'Page ' + page ),
                React.createElement( 'button', {
                    onClick: () => setPage( p => p + 1 ), disabled: loading || filtered.length < PER_PAGE,
                    style: { padding: '8px 20px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' }
                }, 'Next →' )
            ),


            /* NEWS Section — below pagination */
            activeMarketTab === 'coins' && NEWS_ON && React.createElement( 'div', { style: { marginTop: '32px' } },
                React.createElement( 'div', { style: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' } },
                    React.createElement( 'div', { style: { width: '4px', height: '20px', background: 'linear-gradient(135deg,#6C63FF,#FC72FF)', borderRadius: '2px' } } ),
                    React.createElement( 'h3', { style: { margin: 0, fontSize: '16px', fontWeight: '800', color: '#fff' } }, '📰 Crypto News' ),
                    React.createElement( 'span', { style: { fontSize: '12px', color: '#555' } }, 'Latest from news source' )
                ),
                React.createElement( NewsTab, null )
            ),

            adSlotBot && React.createElement( 'div', { dangerouslySetInnerHTML: { __html: adSlotBot } } ),

            selectedCoin && React.createElement( CoinDetailModal, { coin: selectedCoin, onClose: () => setSelectedCoin( null ), livePrices } ),
            selectedListedToken && React.createElement( ListedTokenModal, { token: selectedListedToken, onClose: () => setSelectedListedToken( null ) } )
        )
    );
}

export default VaultMarketPage;
